"use client"

import { ComponentConfig } from "@measured/puck"
import { DefaultImage, configs } from "../../../../external-components"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface FooterProps extends CommonStylesProps {
  url: string
  logoStyles?: {
    width: string
    height: string
    padding: string
    margin: string
    objectFit: "cover" | "contain" | "fill" | "none" | "scale-down"
  }
  footerContainerStyles?: {
    display: "flex" | "block" | "grid" | "inline"
    flexDirection: "row" | "column"
    gap: string
    justifyContent: "flex-start" | "center" | "flex-end" | "space-between"
    className: string
    padding: string
    backgroundColor: string
  }
  footerItemStyles?: {
    titleColor: string
    titleFontSize: string
    titleFontWeight: string
    titleLineHeight: string
    descriptionColor: string
    descriptionFontSize: string
    descriptionFontWeight: string
    descriptionLineHeight: string
    gap: string
  }
  footerItems?: {
    title: string
    description:
      | string
      | Array<{
          url?: string
          description: string
        }>
    iconList?: Array<{
      icon: string
      url: string
    }>
  }[]
  copyRightStyle: {
    padding: string
    backgroundColor: string
    opacity: string
    color: string
    fontSize: string
    fontWeight: string
    lineHeight: string
  }
  copyRight?: string
  type: "fluid" | "custom" | "boxed"
}

export const RenderConfig: ComponentConfig<FooterProps> = {
  render: ({
    url,
    logoStyles = {},
    className,
    type,
    styles,
    copyRight,
    responsiveType,
    footerItems = [],
    footerContainerStyles = {},
    footerItemStyles = {},
    copyRightStyle = {},
  }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`

    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <div className="hidden sm:block">
          <div id={id} className={`${className} `}>
            <div
              className={`${footerContainerStyles?.className}`}
              style={{
                backgroundColor: footerContainerStyles.backgroundColor || "#1D2632",
                display: footerContainerStyles.display || "flex",
                flexDirection: footerContainerStyles.flexDirection || "row",
                gap: footerContainerStyles.gap || "157px",
                justifyContent: footerContainerStyles.justifyContent || "center",
                padding: footerContainerStyles.padding || "48px 220px",
              }}
            >
              <img
                src={url ? `${configs.API_URL}${url}` : DefaultImage}
                style={{
                  width: logoStyles?.width || "234px",
                  height: logoStyles?.height || "78px",
                  objectFit: logoStyles.objectFit || "cover",
                  padding: logoStyles?.padding || "0px",
                  margin: logoStyles?.margin || "0px",
                }}
                className="cursor-pointer"
                alt="Logo"
              />

              {footerItems.length > 0 && (
                <div className="flex flex-row " style={{ gap: footerContainerStyles.gap || "157px" }}>
                  {footerItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col items-start max-w-[249px]"
                      style={{ gap: footerItemStyles.gap || "8px" }}
                    >
                      {item.title && (
                        <h3
                          style={{
                            color: footerItemStyles?.titleColor || "black",
                            fontSize: footerItemStyles.titleFontSize || "14px",
                            fontWeight: footerItemStyles.titleFontWeight || 600,
                            lineHeight: footerItemStyles.titleLineHeight || "22px",
                          }}
                        >
                          {item.title}
                        </h3>
                      )}
                      {item.description && (
                        <div
                          style={{
                            color: footerItemStyles?.descriptionColor || "gray",
                            fontSize: footerItemStyles.descriptionFontSize || "14px",
                            fontWeight: footerItemStyles.descriptionFontWeight || 500,
                            lineHeight: footerItemStyles.descriptionLineHeight || "22px",
                          }}
                        >
                          {Array.isArray(item.description) ? (
                            item.description.map((desc, descIndex) => (
                              <p
                                key={descIndex}
                                onClick={desc.url ? () => window.open(desc.url, "_blank") : undefined}
                                style={{ cursor: desc.url ? "pointer" : "default" }}
                              >
                                {desc.description}
                              </p>
                            ))
                          ) : (
                            <p>{item.description}</p>
                          )}
                        </div>
                      )}
                      {item.iconList && (
                        <div className="flex flex-row gap-3">
                          {item.iconList.map((icon, iconIndex) => (
                            <a href={icon.url || "#"} rel="noopener noreferrer" target="_blank">
                              <img key={iconIndex} src={`${configs.API_URL}${icon.icon}`} alt="Icon" className="h-4" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div
              className="w-full items-center text-center justify-center"
              style={{
                backgroundColor: copyRightStyle.backgroundColor || "#1D2632",
                opacity: copyRightStyle.opacity || "80%",
                padding: copyRightStyle.padding || "12px",
              }}
            >
              <p
                style={{
                  color: copyRightStyle.color || "#AEAEB2",
                  fontSize: copyRightStyle?.fontSize || "12px",
                  fontWeight: copyRightStyle.fontWeight || "500",
                  lineHeight: copyRightStyle.lineHeight || "26px",
                }}
              >
                {copyRight}
              </p>
            </div>
          </div>
        </div>

        <div className="py-10 px-4 bg-[#1D2632] flex flex-col gap-6 w-full sm:hidden">
          <img
            src={url ? `${configs.API_URL}${url}` : DefaultImage}
            style={{
              width: "234px",
              height: "78px",
              objectFit: "cover",
            }}
            className="cursor-pointer"
            alt="Logo"
          />

          <div>
            {footerItems.length > 0 && (
              <div className="flex flex-col gap-6">
                {footerItems.map((item, index) => (
                  <div key={`item ${index}`} className="flex flex-col items-start max-w-[249px] gap-2">
                    {item.title && (
                      <h3 className="text-[#ffffff] text-sm font-semibold leading-[22px]">{item.title}</h3>
                    )}
                    {item.description && (
                      <div className="text-[#C7C7CC] text-sm font-normal leading-[22px]">
                        {Array.isArray(item.description) ? (
                          item.description.map((desc, descIndex) => (
                            <p
                              key={descIndex}
                              onClick={desc.url ? () => window.open(desc.url, "_blank") : undefined}
                              className={desc.url ? "cursor-pointer" : "cursor-default"}
                            >
                              {desc.description}
                            </p>
                          ))
                        ) : (
                          <p>{item.description}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {footerItems.map((item, index) => (
                  <div key={index}>
                    {item.iconList && (
                      <div className="flex flex-row gap-3">
                        {item.iconList.map((icon, iconIndex) => (
                          <a href={icon.url || "#"} rel="noopener noreferrer" target="_blank">
                            <img key={iconIndex} src={`${configs.API_URL}${icon.icon}`} alt="Icon" className="h-4" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    )
  },
}
